<template>
  <v-container fluid>
    <!-- License Overview Section -->
    <v-row class="match-height">
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="body-2 v-card__subtitle">
            <v-icon left small class="mr-2">mdi-chart-bar-stacked</v-icon>
            Lizenzübersicht & Kosten
          </v-card-title>
          <v-card-text>
            <!-- Empty state when no licenses exist -->
            <div v-if="licensesByType.length === 0" class="text-center py-4">
              <v-icon color="grey" class="mb-2">mdi-license</v-icon>
              <div class="grey--text">
                Keine Lizenzen für diese Organisation vorhanden
              </div>
            </div>

            <!-- Bar Chart -->
            <div v-else>
              <VueApexCharts
                type="bar"
                height="180"
                :options="licenseChartOptions"
                :series="licenseChartSeries"
              />

              <!-- Combined Stats & Cost List -->
              <v-list dense nav>
                <v-list-item
                  v-for="licenseGroup in licensesByType"
                  :key="licenseGroup.type"
                  class="py-0"
                  @click="handleFilterLicensesByType(licenseGroup.type)"
                >
                  <v-list-item-content class="">
                    <v-list-item-title class="body-2">
                      {{ licenseGroup.type }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ licenseGroup.active }} aktiv von
                      {{ licenseGroup.total }}
                      <span v-if="licenseGroup.activeCount > 0">
                        · {{ licenseGroup.activeCount }} ×
                        {{
                          getAmountFromCents(
                            licenseGroup.unitPrice || 0,
                            licenseGroup.currency || "EUR",
                          )
                        }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <div class="text-right">
                      <div class="font-weight-medium">
                        {{
                          getAmountFromCents(
                            licenseGroup.totalCost,
                            licenseGroup.currency || "EUR",
                          )
                        }}
                      </div>
                    </div>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <!-- Upcoming Changes Card -->
        <v-card class="mb-4">
          <v-card-title class="body-2 v-card__subtitle">
            <v-icon left small class="mr-2">mdi-clock-outline</v-icon>
            Anstehende Änderungen
          </v-card-title>
          <v-card-text>
            <div v-if="upcomingChanges.length === 0" class="text-center py-4">
              <v-icon color="grey" class="mb-2">mdi-check-circle</v-icon>
              <div class="grey--text">Keine anstehenden Änderungen</div>
            </div>
            <v-list v-else dense>
              <v-list-item
                v-for="change in upcomingChanges"
                :key="change.id"
                class="px-0"
              >
                <v-list-item-icon>
                  <v-icon small :color="getChangeTypeColor(change.type)">
                    {{ getChangeTypeIcon(change.type) }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-2">
                    {{ change.description }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Wirksam ab:
                    {{ timestampConverter(change.effectiveDate) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table
            v-model="selectedLicenses"
            @input="$emit('update:selectedLicenses', $event)"
            :items="flattenedLicensesData"
            :headers="computedLicensesHeaders"
            :search="licensesSearch"
            sort-by="name"
            :group-by="licensesGroupBy"
            :item-key="
              licensesViewMode === 'active' ? 'licenseId' : 'billingEntryKey'
            "
            :mobile-breakpoint="0"
            selectable-key="isSelectable"
            :show-select="true"
            locale="de-DE"
          >
            <template v-slot:top>
              <v-card flat>
                <v-card-subtitle>
                  <v-row class="justify-end" dense>
                    <v-col>
                      <v-icon left small class="mr-2"
                        >mdi-account-credit-card</v-icon
                      >Lizenzverwendung
                      <v-spacer></v-spacer>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn-toggle
                        v-model="licensesViewMode"
                        dense
                        mandatory
                        color="primary"
                      >
                        <v-btn small value="active" outlined>
                          <v-icon small left>mdi-account-check</v-icon>
                          Aktive Lizenzen
                        </v-btn>
                        <v-btn small value="history" outlined>
                          <v-icon small left>mdi-history</v-icon>
                          Abrechnungshistorie
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>
                </v-card-subtitle>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="3" md="3">
                      <v-text-field
                        v-model="licensesSearch"
                        outlined
                        append-icon="mdi-magnify"
                        label="Suchen"
                        :disabled="!licenses.length"
                        hide-details="auto"
                        clearable
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-spacer></v-spacer>

                    <v-col cols="auto">
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-if="hasWriteAccess"
                            depressed
                            v-bind="attrs"
                            v-on="on"
                            :disabled="!licenses.length"
                          >
                            <v-icon left>mdi-dots-vertical</v-icon>
                            Aktionen
                          </v-btn>
                        </template>
                        <v-list nav>
                          <v-list-item
                            v-if="hasWriteAccess"
                            link
                            dense
                            @click="toggleCancelSelectedLicensesDialog()"
                            disabled
                          >
                            <!-- :disabled="licensesViewMode === 'history'" -->
                            <v-list-item-icon>
                              <v-icon dense>mdi-cancel</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title
                              >Ausgewählte Lizenzen kündigen</v-list-item-title
                            >
                          </v-list-item>
                          <v-list-item
                            v-if="hasWriteAccess"
                            link
                            dense
                            @click="toggleChangeSelectedLicensesDialog()"
                            disabled
                          >
                            <!-- :disabled="licensesViewMode === 'history'" -->
                            <v-list-item-icon>
                              <v-icon dense>mdi-swap-horizontal</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title
                              >Ausgewählte Lizenzen wechseln</v-list-item-title
                            >
                          </v-list-item>
                          <v-divider v-if="hasWriteAccess"></v-divider>
                          <v-list-item
                            link
                            dense
                            @click="handleExportLicensesToExcel"
                            :disabled="selectedLicenses.length === 0"
                          >
                            <v-list-item-icon>
                              <v-icon dense>mdi-file-excel</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title
                              >Ausgewählte als Excel
                              exportieren</v-list-item-title
                            >
                          </v-list-item>
                          <!-- <v-list-item
                            link
                            dense
                            @click="handleReconcileLicenseInvoices"
                            :loading="reconcilingLicenses"
                          >
                            <v-list-item-icon>
                              <v-icon dense>mdi-sync</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title
                              >Lizenzen mit Rechnungen
                              abgleichen</v-list-item-title
                            >
                          </v-list-item> -->
                        </v-list>
                      </v-menu>
                    </v-col>
                    <v-col cols="auto">
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            depressed
                            v-bind="attrs"
                            v-on="on"
                            class="mr-2"
                            :disabled="!licenses.length"
                          >
                            <span>
                              Gruppieren nach
                              <strong>{{ licenseFilterText }}</strong>
                            </span>
                            <v-icon right>mdi-menu-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list nav>
                          <v-list-item-group
                            v-model="licensesGroupBy"
                            mandatory
                          >
                            <v-list-item
                              v-for="option in availableGroupingOptions"
                              :key="option.value"
                              link
                              dense
                              :value="option.value"
                            >
                              <v-list-item-icon>
                                <v-icon dense>{{ option.icon }}</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>{{
                                option.text
                              }}</v-list-item-title>
                            </v-list-item>
                          </v-list-item-group>
                        </v-list>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>
            <template v-slot:[`group.header`]="{ items, isOpen, toggle }">
              <th :colspan="licensesHeaders.length + 1">
                <v-icon @click="toggle" class="mr-2"
                  >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
                <span class="font-weight-medium">
                  {{ getGroupHeaderText(items[0]) }}
                </span>
                <span class="ml-1 grey--text font-weight-regular"
                  >({{ items.length }})</span
                >
              </th>
            </template>
            <template v-slot:[`item.user.displayName`]="{ item }">
              <div class="font-weight-medium mt-2">
                {{ item.user.firstName }} {{ item.user.lastName }}
              </div>
              <div style="line-height: 1.2" class="mb-2">
                <small
                  ><strong>UID: </strong><code>{{ item.user.uid }}</code></small
                >

                <div
                  v-if="licensesViewMode === 'active' && item.nextBillingDate"
                >
                  <small>
                    <strong>Nächste Abrechnung: </strong>
                    <span :class="getNextBillingClass(item.nextBillingDate)">
                      {{ timestampConverter(item.nextBillingDate) }}
                    </span>
                  </small>
                </div>
                <div v-if="item.name">
                  <small>
                    <strong>Rechnungsposition: </strong>
                    {{ item.name }}
                  </small>
                </div>
                <div
                  v-if="
                    licensesViewMode === 'active' &&
                    item.billingHistory &&
                    item.billingHistory.length
                  "
                >
                  <small>
                    <strong>Abrechnungshistorie: </strong>
                    <v-chip x-small outlined class="ml-1">
                      <v-icon x-small left>mdi-history</v-icon>
                      {{ item.billingHistory.length }} Einträge
                    </v-chip>
                  </small>
                </div>
              </div>
            </template>
            <template v-slot:[`item.billedAmount`]="{ item }">
              <span class="font-weight-medium">
                {{
                  getAmountFromCents(
                    item.billingEntryBilledAmount || 0,
                    item.currency || "EUR",
                  )
                }}
              </span>
            </template>
            <template v-slot:[`item.prorationInfo`]="{ item }">
              <div
                v-if="
                  item.billingEntrySharedQuantity &&
                  item.billingEntrySharedQuantity > 1
                "
              >
                <div class="font-weight-medium warning--text">
                  <v-icon x-small color="warning" class="mr-1"
                    >mdi-clock-outline</v-icon
                  >
                  Anteilig
                </div>
                <div class="caption grey--text">
                  1 von {{ item.billingEntrySharedQuantity }} Nutzern
                </div>
              </div>
              <div
                v-else-if="item.billingEntryProrated"
                class="font-weight-medium warning--text"
              >
                <v-icon x-small color="warning" class="mr-1"
                  >mdi-clock-outline</v-icon
                >
                Anteilig
              </div>
              <div v-else class="success--text font-weight-medium">
                <v-icon x-small color="success" class="mr-1"
                  >mdi-check-circle</v-icon
                >
                Vollzeitraum
              </div>
            </template>
            <template v-slot:[`item.billedAt`]="{ item }">
              <span v-if="item.billingEntryBilledAt">
                {{ timestampConverter(item.billingEntryBilledAt) }}
              </span>
              <span v-else class="grey--text">N/A</span>
            </template>
            <template v-slot:[`item.license.title`]="{ item }">
              <v-chip outlined small label class="mt-2 font-weight-medium">{{
                item.license?.title || "N/A"
              }}</v-chip>
              <div
                v-if="item.name && licensesViewMode === 'history'"
                class="py-1"
              >
                <small>
                  <strong>Abgerechnet als:</strong>
                  {{ item.name }}
                </small>
              </div>
            </template>
            <template v-slot:[`item.invoiceNumber`]="{ item }">
              <span v-if="item.invoiceNumber" class="text-no-wrap">
                {{ item.invoiceNumber }}
              </span>
              <span v-else class="grey--text">N/A</span>
            </template>
            <template v-slot:[`item.periodRange`]="{ item }">
              <div class="text-no-wrap">
                {{ timestampConverter(item.periodStart) }}
                <span v-if="item.periodEnd"
                  ><v-icon x-small>mdi-arrow-right</v-icon>
                  {{ timestampConverter(item.periodEnd) }}</span
                >
                <span v-else class="success--text font-weight-medium"
                  >(laufend)</span
                >
              </div>
            </template>
            <template v-slot:[`item.totalCost`]="{ item }">
              <span class="font-weight-medium">
                {{
                  getAmountFromCents(
                    item.unitAmount || 0,
                    item.currency || "EUR",
                  )
                }}
              </span>
              <div class="caption grey--text" v-if="item.interval">
                / {{ getBillingCycleText(item.interval) }}
              </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <license-status-chip :status="item.status" small>
              </license-status-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <div v-if="licensesViewMode === 'active'">
                <!-- :disabled="
                    item.status === 'canceled' ||
                    item.status === 'pendingCancellation'
                  " -->
                <v-btn
                  small
                  disabled
                  color="primary"
                  text
                  outlined
                  @click="toggleChangeSelectedLicensesDialog(item)"
                  class="mr-1 my-1"
                >
                  <v-icon small left>mdi-swap-horizontal</v-icon>Wechseln
                </v-btn>
                <!-- :disabled="
                    item.status === 'canceled' ||
                    item.status === 'pendingCancellation'
                  " -->
                <v-btn
                  small
                  depressed
                  outlined
                  text
                  disabled
                  color="error"
                  @click="toggleCancelSelectedLicensesDialog(item)"
                  class="mr-1 my-1"
                >
                  <v-icon small left>mdi-cancel</v-icon>Kündigen
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cancel Licenses Dialog -->
    <CustomDialog
      v-if="cancelSelectedLicensesDialog.model"
      @close="toggleCancelSelectedLicensesDialog()"
      title="Ausgewählte Lizenzen kündigen"
      text="Für ausgewählte Lizenzen wird eine Kündigung zum nächstmöglichen Zeitpunkt im Abrechnungszyklus eingeleitet. Die Lizenzen bleiben bis zum Ende des aktuellen Abrechnungszeitraums aktiv."
      :loading="cancelSelectedLicensesDialog.loading"
      :btnAction="cancelSelectedLicenses"
      :errorMessage="cancelSelectedLicensesDialog.errorMessage"
      width="s"
    ></CustomDialog>

    <!-- Change Licenses Dialog -->
    <CustomDialog
      v-if="changeSelectedLicensesDialog.model"
      @close="toggleChangeSelectedLicensesDialog()"
      title="Ausgewählte Lizenzen wechseln"
      text="Für ausgewählte Lizenzen wird eine Lizenzänderung zum nächstmöglichen Zeitpunkt im Abrechnungszyklus eingeleitet."
      :loading="changeSelectedLicensesDialog.loading"
      :btnAction="changeSelectedLicenses"
      :btnDisabled="!changeSelectedLicensesDialog.productId"
      :errorMessage="changeSelectedLicensesDialog.errorMessage"
      width="s"
    >
      <template v-slot:content>
        <v-form @submit.prevent="changeSelectedLicenses()">
          <v-select
            v-model="changeSelectedLicensesDialog.productId"
            :items="products"
            item-text="name"
            item-value="id"
            item-key="id"
            persistent-hint
            hint="_"
            small-chips
            chips
            label="Neue Lizenz auswählen"
            outlined
            class="mt-2"
          >
            <template v-slot:message>
              Lizenzen bestimmen die Funktionalität, die ein Benutzer innerhalb
              der Organisation nutzen kann. Die Auswahl der Lizenz kann Kosten
              verursachen. Eine detaillierte Auflistung über anfallende Kosten
              ist in unserem
              <a href="#" class="text-decoration-none"
                >Preis- und Leistungsverzeichnis<v-icon
                  x-small
                  class="ml-1"
                  color="primary"
                  >mdi-open-in-new</v-icon
                ></a
              >
              definiert.
            </template>
          </v-select>
        </v-form>
      </template>
    </CustomDialog>
  </v-container>
</template>

<script>
import { db } from "@/firebase";
import LicenseStatusChip from "@/components/admin/billing/LicenseStatusChip.vue";
import CustomDialog from "@/components/_systemwide/CustomDialog.vue";
import VueApexCharts from "vue-apexcharts";
import { billingHelpersMixin } from "@/mixins/billingHelpers";

export default {
  name: "BillingTabLicenses",
  mixins: [billingHelpersMixin],
  components: {
    LicenseStatusChip,
    CustomDialog,
    VueApexCharts,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
    licenses: {
      type: Array,
      default: () => [],
    },
    invoices: {
      type: Array,
      default: () => [],
    },
    hasWriteAccess: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      licensesViewMode: "active", // "active" or "history"
      products: [],
      selectedLicenses: [],
      reconcilingLicenses: false,
      cancelSelectedLicensesDialog: {
        model: false,
        loading: false,
        errorMessage: "",
      },
      changeSelectedLicensesDialog: {
        model: false,
        loading: false,
        errorMessage: "",
        productId: null,
      },
      licensesSearch: "",
      licensesGroupBy: "license.title",
      licensesHeaders: [
        { text: "Benutzerkonto", value: "user.displayName" },
        { text: "Lizenz", value: "license.title" },
        { text: "Rechnung", value: "invoiceNumber" },
        { text: "Leistungszeitraum", value: "periodRange" },
        { text: "Kosten", value: "totalCost", align: "right" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", align: "right", sortable: false },
      ],
      chartColors: [
        "#1976D2", // blue
        "#43A047", // green
        "#FB8C00", // orange
        "#8E24AA", // purple
        "#E53935", // red
        "#00ACC1", // cyan
        "#FFB300", // amber
        "#5E35B1", // deep purple
      ],
    };
  },
  computed: {
    flattenedLicensesData() {
      if (this.licensesViewMode === "active") {
        // Return only active licenses
        return this.licenses.filter(
          (license) =>
            license.status === "active" ||
            license.status === "pendingCancellation",
        );
      } else {
        // Flatten billing history into separate rows
        const flattened = [];
        this.licenses.forEach((license) => {
          if (license.billingHistory && license.billingHistory.length > 0) {
            license.billingHistory.forEach((billing, index) => {
              // Create period range from billing entry
              let billingPeriodRange = "Unbekannt";
              if (billing.periodStart) {
                const startStr = this.timestampConverter(billing.periodStart);
                if (billing.periodEnd) {
                  billingPeriodRange = `${startStr} - ${this.timestampConverter(
                    billing.periodEnd,
                  )}`;
                } else {
                  billingPeriodRange = `${startStr} - Laufend`;
                }
              }

              flattened.push({
                // License metadata
                licenseId: license.licenseId,
                user: license.user,
                // Use license metadata title (manually selected) if available, then product name
                license: {
                  title:
                    billing.license?.title ||
                    billing.productName ||
                    license.license?.title ||
                    "N/A",
                  features: license.license?.features || [],
                },
                name: billing.name || billing.productName || license.name,
                status: billing.status || license.status,
                currency: billing.currency || license.currency,

                // Billing entry data
                billingEntryInvoiceId: billing.invoiceId,
                billingEntryInvoiceNumber: billing.invoiceNumber || "N/A",
                invoiceNumber: billing.invoiceNumber || "N/A", // For grouping
                billingEntryBilledAmount: billing.amount,
                billingEntryPeriodStart: billing.periodStart,
                billingEntryPeriodEnd: billing.periodEnd,
                billingEntryProrated: billing.prorated || false,
                billingEntryProrationFactor: billing.prorationFactor || 0,
                billingEntrySharedQuantity: billing.sharedQuantity || null,
                billingEntryBilledAt: billing.billedAt,
                billingPeriodRange,
                periodRange: billingPeriodRange, // For grouping

                // Unique key for v-data-table
                billingEntryKey: `${license.licenseId}_${billing.invoiceId}_${index}`,
                isSelectable: true, // Allow selection in history mode
              });
            });
          }
        });
        return flattened;
      }
    },
    computedLicensesHeaders() {
      if (this.licensesViewMode === "active") {
        return [
          { text: "Benutzerkonto", value: "user.displayName" },
          { text: "Lizenz", value: "license.title" },
          { text: "Aktuelle Rechnung", value: "invoiceNumber" },
          { text: "Abrechnungszeitraum", value: "periodRange" },
          {
            text: "Kosten",
            value: "totalCost",
            align: "right",
            dividier: true,
          },
          { text: "Status", value: "status" },
          { text: "", value: "actions", align: "right", sortable: false },
        ];
      } else {
        return [
          { text: "Benutzerkonto", value: "user.displayName" },
          { text: "Lizenz", value: "license.title" },
          {
            text: "Rechnung",
            value: "billingEntryInvoiceNumber",
            dividier: true,
          },
          {
            text: "Rechnungsdatum",
            value: "billedAt",
            dividier: true,
          },
          {
            text: "Abrechnungszeitraum",
            value: "billingPeriodRange",
            dividier: true,
          },
          {
            text: "Abgerechneter Betrag",
            value: "billedAmount",
            align: "right",
            dividier: true,
          },
          { text: "Anteilig", value: "prorationInfo" },
          { text: "Status", value: "status" },
        ];
      }
    },
    availableGroupingOptions() {
      if (this.licensesViewMode === "active") {
        return [
          {
            text: "Benutzerkonto",
            value: "user.displayName",
            icon: "mdi-account",
          },
          { text: "Lizenz", value: "license.title", icon: "mdi-license" },
          { text: "Status", value: "status", icon: "mdi-list-status" },
          {
            text: "Rechnung",
            value: "invoiceNumber",
            icon: "mdi-invoice-list",
          },
        ];
      } else {
        return [
          {
            text: "Benutzerkonto",
            value: "user.displayName",
            icon: "mdi-account",
          },
          { text: "Lizenz", value: "license.title", icon: "mdi-license" },
          {
            text: "Rechnung",
            value: "invoiceNumber",
            icon: "mdi-invoice-list",
          },
          { text: "Status", value: "status", icon: "mdi-list-status" },
        ];
      }
    },
    totalLicensesCount() {
      return this.licenses.length;
    },
    upcomingChanges() {
      const changes = [];

      this.licenses
        .filter((license) => license.status === "pendingCancellation")
        .forEach((license) => {
          changes.push({
            id: license.licenseId,
            type: "cancellation",
            description: `Kündigung von ${
              license.license?.title || "Lizenz"
            } für ${license.user?.firstName} ${license.user?.lastName}`,
            effectiveDate: license.cancellationDate || license.periodEnd,
          });
        });

      changes.sort((a, b) => {
        if (!a.effectiveDate || !b.effectiveDate) return 0;
        return a.effectiveDate.toDate() - b.effectiveDate.toDate();
      });

      return changes;
    },
    licensesByType() {
      const grouped = {};

      this.licenses.forEach((license) => {
        const licenseType = license.license?.title || "Unbekannt";
        if (!grouped[licenseType]) {
          grouped[licenseType] = {
            type: licenseType,
            active: 0,
            total: 0,
            pending: 0,
            canceled: 0,
            activeCount: 0,
            unitPrice: 0,
            currency: license.currency || "EUR",
            totalCost: 0,
          };
        }

        grouped[licenseType].total++;

        switch (license.status) {
          case "active":
            grouped[licenseType].active++;
            grouped[licenseType].activeCount++;
            grouped[licenseType].unitPrice = license.unitAmount || 0;
            grouped[licenseType].totalCost += license.unitAmount || 0;
            break;
          case "pendingCancellation":
            grouped[licenseType].pending++;
            break;
          case "canceled":
            grouped[licenseType].canceled++;
            break;
        }
      });

      return Object.values(grouped);
    },
    costBreakdownByLicense() {
      const breakdown = {};

      this.licenses
        .filter((license) => license.status === "active")
        .forEach((license) => {
          const licenseType = license.license?.title || "Unbekannt";
          const unitPrice = license.unitAmount || 0;
          const currency = license.currency || "EUR";

          if (!breakdown[licenseType]) {
            breakdown[licenseType] = {
              licenseType,
              count: 0,
              unitPrice,
              currency,
              totalCost: 0,
            };
          }

          breakdown[licenseType].count++;
          breakdown[licenseType].totalCost += unitPrice;
        });

      return Object.values(breakdown).sort((a, b) => b.totalCost - a.totalCost);
    },
    licenseChartSeries() {
      return this.licensesByType.map((group) => ({
        name: group.type,
        data: [group.total],
      }));
    },
    licenseChartOptions() {
      return {
        chart: {
          type: "bar",
          stacked: true,
          stackType: "100%",
          toolbar: {
            show: false,
          },
        },

        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "80%",
          },
        },
        colors: this.chartColors,
        dataLabels: {
          enabled: true,
          style: {
            colors: ["#fff"],
            fontSize: "12px",
            fontWeight: "bold",
          },
        },
        xaxis: {
          categories: ["Lizenzen"],
          labels: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            show: false,
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          y: {
            formatter: (val) => {
              return val + " Lizenzen";
            },
          },
        },
      };
    },
    licenseFilterText() {
      if (this.licensesGroupBy === "user.displayName") {
        return "Benutzerkonto";
      } else if (this.licensesGroupBy === "status") {
        return "Status";
      } else if (this.licensesGroupBy === "license.title") {
        return "Lizenz";
      } else if (this.licensesGroupBy === "invoiceNumber") {
        return "Rechnung";
      } else {
        return "Fehler";
      }
    },
  },
  created() {
    this.getProducts();
  },
  methods: {
    getProducts() {
      db.collection("products")
        .get()
        .then((snapshot) => {
          this.products = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        })
        .catch((error) => {
          console.error("Error getting products:", error);
        });
    },
    timestampConverter(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
    getAmountFromCents(amount, currency) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency,
      }).format(amount / 100);
    },
    getBillingCycleText(cycle) {
      const cycles = {
        month: "Monat",
        year: "Jahr",
        week: "Woche",
        day: "Tag",
      };
      return cycles[cycle] || cycle;
    },
    getNextBillingClass(nextBillingDate) {
      if (!nextBillingDate) return "";

      const now = new Date();
      const billingDate = nextBillingDate.toDate();
      const daysUntilBilling = Math.ceil(
        (billingDate - now) / (1000 * 60 * 60 * 24),
      );

      if (daysUntilBilling <= 7) {
        return "warning--text font-weight-medium";
      } else if (daysUntilBilling <= 30) {
        return "info--text";
      }
      return "";
    },
    getChangeTypeIcon(changeType) {
      const icons = {
        cancellation: "mdi-cancel",
        upgrade: "mdi-arrow-up-circle",
        downgrade: "mdi-arrow-down-circle",
        renewal: "mdi-refresh",
      };
      return icons[changeType] || "mdi-information";
    },
    getChangeTypeColor(changeType) {
      const colors = {
        cancellation: "error",
        upgrade: "success",
        downgrade: "warning",
        renewal: "primary",
      };
      return colors[changeType] || "grey";
    },
    getGroupHeaderText(item) {
      // Get the displayed value based on current grouping
      const groupBy = this.licensesGroupBy;

      if (groupBy === "user.displayName") {
        return `${item.user?.firstName || ""} ${item.user?.lastName || ""}`;
      } else if (groupBy === "status") {
        return this.getStatusText(item.status);
      } else if (groupBy === "license.title") {
        return item.license?.title || "N/A";
      } else if (groupBy === "invoiceNumber") {
        return item.invoiceNumber || "Keine Rechnung";
      }

      return "Unbekannt";
    },
    getStatusText(status) {
      const statusMap = {
        active: "Aktiv",
        canceled: "Gekündigt",
        pendingCancellation: "Kündigung ausstehend",
        expired: "Abgelaufen",
        trialing: "Testversion",
      };
      return statusMap[status] || status || "N/A";
    },
    getInvoiceById(invoiceId) {
      return this.invoices.find((inv) => inv.id === invoiceId) || null;
    },
    handleFilterLicensesByType(licenseType) {
      // Set the groupBy to license type and apply search filter
      this.licensesGroupBy = "license.title";
      this.licensesSearch = licenseType;
    },
    handleShowLicenseFeatures(license) {
      // You can implement a dialog to show license features
      const features = license.features || [];
      const featureText =
        features.length > 0 ? features.join("\n• ") : "Keine Features";
      alert(`Features für ${license.title}:\n\n• ${featureText}`);
    },
    async handleExportLicensesToExcel() {
      try {
        // Check if there is data to export
        if (this.selectedLicenses.length === 0) {
          this.$store.commit("snackbar/setSnackbar", {
            type: "warning",
            message:
              this.licensesViewMode === "history"
                ? "Bitte wählen Sie Abrechnungen aus, um sie zu exportieren"
                : "Bitte wählen Sie Lizenzen aus, um sie zu exportieren",
          });
          return;
        }

        const dataToExport = this.selectedLicenses;

        // Dynamic import of ExcelJS
        const ExcelJS = (await import("exceljs")).default;

        const workbook = new ExcelJS.Workbook();
        workbook.creator = "Mira One";
        workbook.created = new Date();

        const worksheetName =
          this.licensesViewMode === "active"
            ? "Aktive Lizenzen"
            : "Abrechnungshistorie";
        const worksheet = workbook.addWorksheet(worksheetName);

        // Use consistent table structure for both modes
        worksheet.columns = [
          { header: "Lizenz-ID", key: "licenseId", width: 25 },
          { header: "Benutzer", key: "userName", width: 30 },
          { header: "Lizenztyp", key: "licenseType", width: 20 },
          { header: "Rechnungsposition", key: "lineItemName", width: 30 },
          { header: "Rechnung", key: "invoiceNumber", width: 20 },
          { header: "Rechnungs-ID", key: "invoiceId", width: 25 },
          {
            header: "Abrechnungszeitraum Start",
            key: "periodStart",
            width: 20,
          },
          { header: "Abrechnungszeitraum Ende", key: "periodEnd", width: 20 },
          { header: "Betrag", key: "amount", width: 15 },
          { header: "Anteilig", key: "prorated", width: 10 },
          { header: "Abrechnungsdatum", key: "billedAt", width: 20 },
          { header: "Status", key: "status", width: 15 },
        ];

        // Add data - same structure for both active and history modes
        dataToExport.forEach((entry, index) => {
          const statusValue = entry.status;
          const statusText = this.getStatusText(statusValue) || "N/A";

          const row = worksheet.addRow({
            licenseId: entry.licenseId || "N/A",
            userName: entry.user?.displayName || "N/A",
            licenseType: entry.license?.title || "N/A",
            lineItemName: entry.name || "N/A",
            invoiceNumber:
              this.licensesViewMode === "active"
                ? entry.invoiceNumber || "N/A"
                : entry.billingEntryInvoiceNumber || "N/A",
            invoiceId:
              this.licensesViewMode === "active"
                ? "N/A"
                : entry.billingEntryInvoiceId || "N/A",
            periodStart:
              this.licensesViewMode === "active"
                ? entry.periodStart
                  ? this.timestampConverter(entry.periodStart)
                  : "N/A"
                : entry.billingEntryPeriodStart
                ? this.timestampConverter(entry.billingEntryPeriodStart)
                : "N/A",
            periodEnd:
              this.licensesViewMode === "active"
                ? entry.periodEnd
                  ? this.timestampConverter(entry.periodEnd)
                  : "Laufend"
                : entry.billingEntryPeriodEnd
                ? this.timestampConverter(entry.billingEntryPeriodEnd)
                : "Laufend",
            amount:
              this.licensesViewMode === "active"
                ? (entry.unitAmount || 0) / 100
                : (entry.billingEntryBilledAmount || 0) / 100,
            prorated:
              this.licensesViewMode === "active"
                ? "N/A"
                : entry.billingEntryProrated
                ? "Ja"
                : "Nein",
            billedAt:
              this.licensesViewMode === "active"
                ? "N/A"
                : entry.billingEntryBilledAt
                ? this.timestampConverter(entry.billingEntryBilledAt)
                : "N/A",
            status: statusText,
          });

          // Apply alternating row background colors
          const fillColor = index % 2 === 0 ? "FFF8F9FA" : "FFFFFFFF";
          row.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: fillColor },
            };
            cell.border = {
              top: { style: "thin", color: { argb: "FF333333" } },
              left: { style: "thin", color: { argb: "FF333333" } },
              bottom: { style: "thin", color: { argb: "FF333333" } },
              right: { style: "thin", color: { argb: "FF333333" } },
            };
          });

          // Format amount column as currency
          const amountCell = row.getCell(9); // "Betrag" column
          amountCell.numFmt = '#,##0.00 "€"';
          amountCell.alignment = { horizontal: "right" };

          // Highlight important columns with subtle color
          row.getCell(4).fill = {
            // Rechnungsposition
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFF3CD" },
          };
          row.getCell(9).fill = {
            // Betrag
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE7F3FF" },
          };

          // Apply color to status cell based on status value
          const statusCell = row.getCell(12); // "Status" column
          let statusColor = "FFFFFFFF"; // Default white
          if (statusValue === "active") {
            statusColor = "FFC8E6C9"; // Light green
          } else if (statusValue === "canceled") {
            statusColor = "FFFFCDD2"; // Light red
          } else if (statusValue === "pendingCancellation") {
            statusColor = "FFFFE0B2"; // Light orange
          } else if (statusValue === "expired") {
            statusColor = "FFE0E0E0"; // Light gray
          } else if (statusValue === "trialing") {
            statusColor = "FFB3E5FC"; // Light blue
          }

          statusCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: statusColor },
          };
        });

        // Style header row - only color individual cells, not the entire row
        const headerRow = worksheet.getRow(1);
        headerRow.height = 25;
        headerRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1976D2" },
          };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
          };
          cell.border = {
            top: { style: "thin", color: { argb: "FF333333" } },
            left: { style: "thin", color: { argb: "FF333333" } },
            bottom: { style: "thin", color: { argb: "FF333333" } },
            right: { style: "thin", color: { argb: "FF333333" } },
          };
        });

        // Add Excel Table with auto-filter for sorting and filtering
        worksheet.addTable({
          name: "LicenseTable",
          ref: `A1:L${dataToExport.length + 1}`,
          headerRow: true,
          totalsRow: false,
          style: {
            theme: "TableStyleLight9",
            showRowStripes: false, // We handle alternating colors manually
          },
          columns: [
            { name: "Lizenz-ID" },
            { name: "Benutzer" },
            { name: "Lizenztyp" },
            { name: "Rechnungsposition" },
            { name: "Rechnung" },
            { name: "Rechnungs-ID" },
            { name: "Abrechnungszeitraum Start" },
            { name: "Abrechnungszeitraum Ende" },
            { name: "Betrag" },
            { name: "Anteilig" },
            { name: "Abrechnungsdatum" },
            { name: "Status" },
          ],
          rows: [], // Data already added via addRow
        });

        // Auto-fit columns
        worksheet.columns.forEach((column) => {
          const maxLength = column.values.reduce((max, val) => {
            const length = val ? val.toString().length : 0;
            return Math.max(max, length);
          }, 10);
          column.width = Math.min(maxLength + 2, 50);
        });

        // Generate Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Download file
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const date = new Date().toISOString().slice(0, 10);
        link.download = `Abrechnungsexport-${date}.xlsx`;
        link.click();

        this.$store.commit("snackbar/setSnackbar", {
          type: "success",
          message: `${dataToExport.length} ${
            this.licensesViewMode === "active" ? "Lizenzen" : "Abrechnungen"
          } erfolgreich exportiert`,
        });
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$store.commit("snackbar/setSnackbar", {
          type: "error",
          message: "Fehler beim Exportieren: " + error.message,
        });
      }
    },
    async handleReconcileLicenseInvoices() {
      this.reconcilingLicenses = true;
      try {
        const { functions } = await import("@/firebase");
        const reconcileFunction = functions.httpsCallable(
          "scheduled-reconcileCustomerLicenses",
        );

        const result = await reconcileFunction({
          customerId: this.customerId,
        });

        if (result.data.success) {
          this.$store.commit("snackbar/setSnackbar", {
            type: "success",
            message: `Abgleich abgeschlossen: ${result.data.licensesLinked} Lizenzen mit Rechnungen verknüpft`,
          });

          // Reload licenses to show updated billing history
          await this.getLicenses();
        } else {
          this.$store.commit("snackbar/setSnackbar", {
            type: "warning",
            message:
              "Abgleich mit Fehlern abgeschlossen. Siehe Console für Details.",
          });
          console.warn("Reconciliation errors:", result.data.errors);
        }
      } catch (error) {
        console.error("Error reconciling licenses:", error);
        this.$store.commit("snackbar/setSnackbar", {
          type: "error",
          message: "Fehler beim Abgleich der Lizenzen: " + error.message,
        });
      } finally {
        this.reconcilingLicenses = false;
      }
    },
    toggleCancelSelectedLicensesDialog(item) {
      if (item) {
        this.selectedLicenses = [item];
      }
      this.cancelSelectedLicensesDialog.model =
        !this.cancelSelectedLicensesDialog.model;

      // Reset error message when closing
      if (!this.cancelSelectedLicensesDialog.model) {
        this.cancelSelectedLicensesDialog.errorMessage = "";
      }
    },
    toggleChangeSelectedLicensesDialog(item) {
      if (item) {
        this.selectedLicenses = [item];
      }
      this.changeSelectedLicensesDialog.model =
        !this.changeSelectedLicensesDialog.model;

      // Reset error message and selection when closing
      if (!this.changeSelectedLicensesDialog.model) {
        this.changeSelectedLicensesDialog.errorMessage = "";
        this.changeSelectedLicensesDialog.productId = null;
      }
    },
    async cancelSelectedLicenses() {
      this.cancelSelectedLicensesDialog.loading = true;
      this.cancelSelectedLicensesDialog.errorMessage = "";

      if (!this.customerId) {
        this.cancelSelectedLicensesDialog.errorMessage =
          "Kundendaten nicht verfügbar";
        this.cancelSelectedLicensesDialog.loading = false;
        return;
      }

      try {
        const { functions } = await import("@/firebase");
        const callFunction = functions.httpsCallable(
          "stripeBilling-cancelLicenses",
        );

        const result = await callFunction({
          customerId: this.customerId,
          licenseIds: this.selectedLicenses.map((license) => license.licenseId),
        });

        if (result.data.success) {
          this.cancelSelectedLicensesDialog.loading = false;
          this.toggleCancelSelectedLicensesDialog();

          // Reload licenses
          await this.getLicenses();

          this.$store.commit("snackbar/setSnackbar", {
            type: "success",
            message: "Lizenzen erfolgreich gekündigt",
          });
        } else {
          console.log(result.data);
          this.cancelSelectedLicensesDialog.errorMessage =
            result.data.error?.message || "Fehler beim Kündigen der Lizenzen";
          this.cancelSelectedLicensesDialog.loading = false;
        }
      } catch (error) {
        console.error(error);
        this.cancelSelectedLicensesDialog.errorMessage = error.message;
        this.cancelSelectedLicensesDialog.loading = false;
      }
    },
    async changeSelectedLicenses() {
      this.changeSelectedLicensesDialog.loading = true;
      this.changeSelectedLicensesDialog.errorMessage = "";

      try {
        const { functions } = await import("@/firebase");
        const callFunction = functions.httpsCallable(
          "stripeBilling-changeLicenses",
        );

        const result = await callFunction({
          organizationId: this.$route.params.organizationId,
          productId: this.changeSelectedLicensesDialog.productId,
          licenseIds: this.selectedLicenses.map((license) => license.licenseId),
        });

        if (result.data.success) {
          this.changeSelectedLicensesDialog.loading = false;
          this.toggleChangeSelectedLicensesDialog();

          // Reload licenses
          await this.getLicenses();

          this.$store.commit("snackbar/setSnackbar", {
            type: "success",
            message: "Lizenzen erfolgreich geändert",
          });
        } else {
          this.changeSelectedLicensesDialog.errorMessage =
            "Fehler beim Wechseln der Lizenzen, kontaktieren Sie den Support";
          this.changeSelectedLicensesDialog.loading = false;
        }
      } catch (error) {
        console.error(error.data);
        console.error(error);
        this.changeSelectedLicensesDialog.errorMessage = error.message;
        this.changeSelectedLicensesDialog.loading = false;
      }
    },
  },
};
</script>
